import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, Typography, Avatar, IconButton, Dialog, DialogContent, 
  TextField, InputAdornment, Grid, CardActionArea, Card, Chip,
  CircularProgress, Stack, Tabs, Tab, Divider, Button, Snackbar, Alert
} from '@mui/material';
import { Search, Close, LocationOn, ErrorOutline, Construction, CheckCircle, AddBusiness } from '@mui/icons-material';
import { CreateCompanyModal } from './CreateCompanyModal'; 
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import OpacityIcon from '@mui/icons-material/Opacity';

export const CompanySelectorModal = ({ open, onClose, onSelect, currentId }) => {
  const [activeTab, setActiveTab] = useState('FUEL');
  const [searchQuery, setSearchQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [feedback, setFeedback] = useState({ open: false, msg: '', severity: 'success' });

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!open) return;
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token'); 
        const response = await fetch('https://console.dreamtec.io/customers', {
          method: 'GET',
          headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch Company list');
        const data = await response.json();
        setCompanies(data);
      } catch (err) { setError(err.message); } 
      finally { setLoading(false); }
    };
    fetchCompanies();
  }, [open, refreshTrigger]);

  const finalCompanies = useMemo(() => {
    const filtered = companies.filter(c => {
      const matchesType = (c.customer_type?.toUpperCase() || 'FUEL') === activeTab;
      const matchesSearch = 
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.cid?.toString().includes(searchQuery);
      return matchesType && matchesSearch;
    });

    const demoCompany = filtered.find(c => c.cid === 1000);
    const otherCompanies = filtered.filter(c => c.cid !== 1000);
    return demoCompany ? [demoCompany, ...otherCompanies] : otherCompanies;
  }, [searchQuery, companies, activeTab]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md" 
      PaperProps={{ sx: { borderRadius: 3, height: '85vh', bgcolor: '#F4FAFB' } }}
    >
      
      {/* ─── HEADER SECTION ─── */}
      <Box sx={{ p: 3, pb: 2, bgcolor: '#fff', borderBottom: '1px solid #E2E8F0' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1A3A3F' }}>
                Select Company
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose} 
            size="small" 
           
          >
            <Close />
          </IconButton>
        </Stack>

        {/* ─── ACTION ROW (NEW COMPANY BUTTON) ─── */}
        <Box sx={{ display:'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            startIcon={<AddBusiness />} 
            onClick={() => setCompanyModalOpen(true)}
            color="primary"
          
          >
            New Company
          </Button>
        </Box>

        <Tabs 
            value={activeTab} 
            onChange={(e, v) => { setActiveTab(v); setSearchQuery(''); }} 
            sx={{ mb: 1, '& .MuiTabs-indicator': { backgroundColor: '#7dd3db', height: 3 } }}
        >
          <Tab value="FUEL" icon={<OpacityIcon fontSize="small" />} iconPosition="start" label="FUEL" sx={{ fontWeight: 800 }} />
          <Tab value="FOOD" icon={<LunchDiningIcon fontSize="small" />} iconPosition="start" label="FOOD" sx={{ fontWeight: 800 }} />
        </Tabs>
        
        <Box>
            <TextField 
                fullWidth 
                placeholder={`Search ${activeTab.toLowerCase()} registry by name or CID...`} 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                disabled={loading}
                InputProps={{ 
                    startAdornment: <InputAdornment position="start"><Search sx={{ color: '#7dd3db' }} /></InputAdornment>, 
                    sx: { borderRadius: '12px', bgcolor: '#f8fafc', fontWeight: 600 } 
                }}
            />
        </Box>
      </Box>

      {/* ─── GRID CONTENT ─── */}
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 15 }}>
            <CircularProgress size={32} thickness={5} sx={{ color: '#2F727C' }} />
            <Typography sx={{ mt: 2, fontWeight: 700, color: '#94a3b8', fontSize: '0.75rem' }}>LOADING REGISTRY...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <ErrorOutline sx={{ fontSize: 48, color: '#ef4444', opacity: 0.5 }} />
            <Typography sx={{ mt: 2, fontWeight: 700 }}>{error}</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {finalCompanies.map((company) => {
              const isSelected = company.cid === currentId;
              const isDemo = company.cid === 1000;
              
              return (
                <Grid item xs={12} sm={6} md={4} key={company.cid}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderRadius: 3, transition: 'all 0.2s',
                      border: isDemo 
                        ? (isSelected ? '2.5px solid #2F727C' : '1px solid #2F727C') 
                        : (isSelected ? '2px solid #7dd3db' : '1px solid #e2e8f0'),
                      bgcolor: isDemo 
                        ? (isSelected ? '#e0f2f1' : '#f8fafc') 
                        : (isSelected ? '#fffdf7' : '#fff'),
                      boxShadow: isSelected ? '0 8px 20px rgba(0,0,0,0.1)' : 'none',
                      '&:hover': { transform: 'translateY(-3px)', borderColor: isDemo ? '#1e4b52' : '#7dd3db' }
                    }}
                  >
                    <CardActionArea onClick={() => onSelect(company)} sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ 
                            bgcolor: isDemo ? '#2F727C' : '#7dd3db', 
                            color: '#fff', width: 44, height: 44, fontWeight: 900,
                            boxShadow: isSelected ? 2 : 0
                        }}>
                          {isDemo ? <Construction /> : (company.name ? company.name.charAt(0).toUpperCase() : '?')}
                        </Avatar>
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                          <Typography noWrap sx={{ fontWeight: 900, fontSize: '0.85rem', color: isDemo ? '#2F727C' : '#1A3A3F' }}>
                            {company.name}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <LocationOn sx={{ fontSize: 12, color: isDemo ? '#2F727C' : '#94a3b8' }} />
                            <Typography sx={{ fontSize: '0.7rem', color: isDemo ? '#2F727C' : '#64748b', fontWeight: 800 }}>
                              {isDemo ? 'Global' : (company.country || 'Ireland')}
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>

                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                            label={`CID: ${company.cid}`} 
                            size="small" 
                            sx={{ height: 22, fontSize: '0.6rem', fontWeight: 800, bgcolor: isSelected ? '#1A3A3F' : '#f1f5f9', color: isSelected ?'#fff' : '#2F727C' }} 
                        />
                        {isSelected && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <CheckCircle sx={{ fontSize: 14, color: isDemo ? '#2F727C' : '#7dd3db' }} />
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: isDemo ? '#2F727C' : '#7dd3db' }}>SELECTED</Typography>
                          </Stack>
                        )}
                      </Box>
                    </CardActionArea>
                  </Card>

                  {isDemo && finalCompanies.length > 1 && searchQuery === '' && (
                      <Box sx={{ gridColumn: '1 / -1', mt: 2, mb: 1, width: '100%' }}>
                          <Divider><Typography variant="caption" sx={{ fontWeight: 800, color: '#cbd5e1', letterSpacing: 1 }}>CLIENT REGISTRY</Typography></Divider>
                      </Box>
                  )}
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      {/* ─── MODALS & FEEDBACK ─── */}
      <CreateCompanyModal 
        open={companyModalOpen} 
        onClose={() => setCompanyModalOpen(false)} 
        onSuccess={() => {
            setRefreshTrigger(prev => prev + 1);
            setFeedback({ open: true, msg: 'New company provisioned successfully', severity: 'success' });
        }}
      />

      <Snackbar open={feedback.open} autoHideDuration={3000} onClose={() => setFeedback({...feedback, open: false})}>
        <Alert severity={feedback.severity} variant="filled" sx={{ fontWeight: 700, borderRadius: '10px' }}>{feedback.msg}</Alert>
      </Snackbar>

    </Dialog>
  );
};